import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Divider, Paper, Stack, Typography} from 'views/components';
import {MessageBubble, MessageComposer, Translate, translate} from 'views/module';

import backend from 'utils/backend';
import {shortDateTime} from 'utils/translate/date_formatter';

const ClientChats = (props) => {
  const caregiverRn = props.caregiverRn;
  const caregiverData = props.caregiverData;
  const caregiverBackend = props.caregiverBackend;
  const caregiverChatConversations = props.caregiverChatConversations;
  const caregiverChatConversationsLoading = props.caregiverChatConversationsLoading;
  const caregiverChatMessages = props.caregiverChatMessages;
  const caregiverChatMessagesLoading = props.caregiverChatMessagesLoading;
  const filterClientRn = props.filterClientRn;
  const includeSupportChats = props.includeSupportChats;
  const user = props.user;
  const searchParams = new URLSearchParams(window.location.search);
  const targetConversationRn = searchParams.get('conversationRn');
  const targetMessageRn = searchParams.get('messageRn');

  const [expandedConversationRn, setExpandedConversationRn] = React.useState(targetConversationRn);
  const [isCreatingSupportChat, setIsCreatingSupportChat] = React.useState(false);
  const [createSupportChatFailed, setCreateSupportChatFailed] = React.useState(false);
  const [highlightTargetMessage, setHighlightTargetMessage] = React.useState(Boolean(targetMessageRn));
  const messageContainerRefs = React.useRef({});
  const messageRefs = React.useRef({});
  const hasFocusedTargetMessageRef = React.useRef(false);

  const conversations = caregiverChatConversations || [];
  const messagesByConversation = caregiverChatMessages || {};
  const messagesLoadingByConversation = caregiverChatMessagesLoading || {};
  const conversationsLoading = Boolean(caregiverChatConversationsLoading);

  const clients = React.useMemo(() => {
    return Object.values(caregiverData?.clients || {});
  }, [caregiverData]);

  React.useEffect(() => {
    caregiverBackend.chat.loadConversations();
  }, [caregiverRn]);

  React.useEffect(() => {
    if (!targetConversationRn || conversations.length === 0) {
      return;
    }

    const conversationExists = conversations.some((conversation) => conversation.conversationRn === targetConversationRn);
    if (!conversationExists) {
      return;
    }

    setExpandedConversationRn(targetConversationRn);
    if (!messagesByConversation[targetConversationRn] && !messagesLoadingByConversation[targetConversationRn]) {
      caregiverBackend.chat.loadConversationMessages(targetConversationRn);
    }
  }, [conversations, targetConversationRn]);

  const chatList = React.useMemo(() => {
    const output = conversations.map((conversation) => {
      const conversationRn = conversation.conversationRn;
      const latestMessage = conversation.latestMessage;
      const latestMessageCreatedAt = latestMessage?.createdAt;

      if (conversationRn.includes('SUPPORT')) {
        return {
          conversationRn,
          conversation,
          isSupport: true,
          isFamilyChat: false,
          clientRns: [],
          title: translate('caregiver.CaregiverDetail.ClientConnections.SupportChat.ChatLabel'),
          latestMessage,
          latestMessageCreatedAt,
        };
      }

      const participants = conversation.participants.filter((participant) => {
        return participant.userRn !== caregiverRn;
      });

      if (participants.length === 0) {
        return null;
      }

      const relationshipParticipants = participants.filter((participant) => {
        return participant.userRn.includes(':RELATIONSHIP-');
      });

      if (relationshipParticipants.length === 0) {
        return null;
      }

      const relatedRelationships = relationshipParticipants.map((participant) => {
        const relationshipRn = participant.userRn;
        const clients = Object.values(caregiverData.clients);

        let relationship;
        let relationshipClientRn;
        let clientName;

        clients.forEach((client) => {
          const relationships = client.clientDetail.relationships;
          if (relationships[relationshipRn]) {
            relationship = relationships[relationshipRn];
            relationshipClientRn = client.clientRn;
            clientName = client.clientDetail.clientName;
          }
        });

        const name = relationship?.name;

        return {
          relationshipRn: participant.userRn,
          relationshipClientRn,
          name,
          connectionType: relationship?.connectionType,
          clientName,
        };
      });

      const relationshipNames = relatedRelationships.map((relationship) => relationship?.name);
      const clientRns = [...new Set(relatedRelationships.map((relationship) => relationship.relationshipClientRn))];
      const isFamilyChat = relationshipNames.length > 1;
      const primaryRelationshipName = relationshipNames[0] || translate('caregiver.CaregiverDetail.ClientConnections.Chat.UnknownRelationship');
      const primaryConnectionType = relatedRelationships[0]?.connectionType;
      const title = isFamilyChat ?
        `${translate('caregiver.CaregiverDetail.ClientConnections.Chat.Family')} (${relationshipNames.filter(Boolean).join(', ')})` :
        [primaryRelationshipName, primaryConnectionType].filter(Boolean).join(', ');

      return {
        conversationRn,
        conversation,
        isSupport: false,
        isFamilyChat,
        clientRns,
        title,
        latestMessage,
        latestMessageCreatedAt,
      };
    }).filter(Boolean);

    return output.sort((first, second) => {
      const firstTime = first.latestMessageCreatedAt ? new Date(first.latestMessageCreatedAt).getTime() : 0;
      const secondTime = second.latestMessageCreatedAt ? new Date(second.latestMessageCreatedAt).getTime() : 0;
      return secondTime - firstTime;
    });
  }, [caregiverRn, conversations]);

  const supportChats = React.useMemo(() => {
    if (!includeSupportChats) {
      return [];
    }

    return chatList.filter((chat) => chat?.isSupport);
  }, [chatList, includeSupportChats]);

  const nonSupportChats = React.useMemo(() => {
    return chatList.filter((chat) => !chat?.isSupport);
  }, [chatList]);

  const clientSections = React.useMemo(() => {
    return clients.filter((client) => {
      return !filterClientRn || client?.clientRn === filterClientRn;
    }).map((client) => {
      const clientRn = client?.clientRn;
      const clientName = client?.clientDetail?.clientName;

      const careRecipientName = client?.careplan?.generalInformation?.clientName;

      const clientChats = nonSupportChats.filter((chat) => {
        return chat.clientRns.includes(clientRn);
      });
      const sortedClientChats = [
        ...clientChats.filter((chat) => chat.isFamilyChat),
        ...clientChats.filter((chat) => !chat.isFamilyChat),
      ];

      return {
        clientRn,
        clientName,
        careRecipientName,
        chats: sortedClientChats,
      };
    }).filter((section) => section.chats.length > 0);
  }, [clients, filterClientRn, nonSupportChats]);

  const unassignedChats = React.useMemo(() => {
    if (filterClientRn) {
      return [];
    }

    const mappedConversations = new Set(
        clientSections
            .flatMap((section) => section.chats)
            .map((chat) => chat.conversationRn),
    );

    return nonSupportChats.filter((chat) => !mappedConversations.has(chat.conversationRn));
  }, [clientSections, filterClientRn, nonSupportChats]);

  const visibleChatCount = React.useMemo(() => {
    return supportChats.length +
      clientSections.reduce((count, section) => count + section.chats.length, 0) +
      unassignedChats.length;
  }, [clientSections, supportChats, unassignedChats]);

  const handleAccordionChange = (conversationRn) => (event, expanded) => {
    setExpandedConversationRn(expanded ? conversationRn : null);

    if (!expanded) {
      return;
    }

    if (messagesByConversation[conversationRn]) {
      return;
    }

    caregiverBackend.chat.loadConversationMessages(conversationRn);
  };

  React.useEffect(() => {
    if (!expandedConversationRn) {
      return;
    }

    const messageContainer = messageContainerRefs.current[expandedConversationRn];
    if (!messageContainer) {
      return;
    }

    if (targetMessageRn) {
      if (!messageRefs.current[targetMessageRn] || hasFocusedTargetMessageRef.current) {
        return undefined;
      }

      const animationFrame = window.requestAnimationFrame(() => {
        const targetMessage = messageRefs.current[targetMessageRn];
        const containerBounds = messageContainer.getBoundingClientRect();
        const messageBounds = targetMessage?.getBoundingClientRect();

        hasFocusedTargetMessageRef.current = true;
        if (messageBounds) {
          const messageOffset = messageBounds.top - containerBounds.top;
          const centeredOffset = messageOffset - ((messageContainer.clientHeight - messageBounds.height) / 2);
          messageContainer.scrollTop += centeredOffset;
        }
      });
      const highlightTimer = window.setTimeout(() => setHighlightTargetMessage(false), 4000);
      return () => {
        window.cancelAnimationFrame(animationFrame);
        window.clearTimeout(highlightTimer);
      };
    }

    messageContainer.scrollTop = messageContainer.scrollHeight;
    return undefined;
  }, [expandedConversationRn, messagesByConversation, targetMessageRn]);

  const createSupportChat = async () => {
    if (isCreatingSupportChat) {
      return;
    }

    setIsCreatingSupportChat(true);
    setCreateSupportChatFailed(false);

    try {
      const output = await backend.communication.createChannel(caregiverRn, 'SUPPORT');
      const conversationRn = output?.conversationRn;

      await caregiverBackend.chat.loadConversations();

      if (conversationRn) {
        setExpandedConversationRn(conversationRn);
        caregiverBackend.chat.loadConversationMessages(conversationRn);
      }
    } catch (error) {
      console.error('Failed to create the support chat:', error);
      setCreateSupportChatFailed(true);
    } finally {
      setIsCreatingSupportChat(false);
    }
  };

  const sendMessage = async (body) => {
    await caregiverBackend.chat.sendConversationMessage(body);
    await caregiverBackend.chat.loadConversationMessages(body.conversationRn);
  };

  const getLatestMessageText = (chat) => {
    const latestMessage = chat.latestMessage;

    if (latestMessage?.messageText) {
      return latestMessage.messageText;
    }

    if (latestMessage?.files?.length > 0) {
      return translate('caregiver.CaregiverDetail.ClientConnections.Chat.Attachment');
    }

    return translate('caregiver.CaregiverDetail.ClientConnections.Chat.NoMessages');
  };

  const renderChatAccordion = (chat) => {
    const conversationMessages = messagesByConversation[chat.conversationRn] || [];
    const hasLoadedMessages = messagesByConversation[chat.conversationRn];
    const isLoadingMessages = Boolean(messagesLoadingByConversation[chat.conversationRn]);
    const messagesToDisplay = [...conversationMessages].sort((first, second) => {
      const firstTime = first?.createdAt ? new Date(first.createdAt).getTime() : 0;
      const secondTime = second?.createdAt ? new Date(second.createdAt).getTime() : 0;
      return firstTime - secondTime;
    });

    return (
      <Accordion
        key={chat.conversationRn}
        expanded={expandedConversationRn === chat.conversationRn}
        onChange={handleAccordionChange(chat.conversationRn)}
        sx={{
          'boxShadow': 'none',
          '&:before': {
            display: 'none',
          },
          'borderBottom': '1px solid',
          'borderColor': 'divider',
        }}
      >
        <AccordionSummary
          sx={{
            'px': 2,
            'py': 1,
            '& .MuiAccordionSummary-content': {
              ml: 2,
              my: 0,
            },
          }}
        >
          <Stack sx={{minWidth: 0}}>
            <Typography sx={{fontWeight: 600}}>
              {chat.title}
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
              }}
            >
              {getLatestMessageText(chat)}
            </Typography>
            {
              (() => {
                if (!chat?.latestMessageCreatedAt) {
                  return null;
                }

                return (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: 'text.secondary',
                    }}
                  >
                    {shortDateTime(chat?.latestMessageCreatedAt)}
                  </Typography>
                );
              })()
            }
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            px: 2,
            pb: 2,
          }}
        >
          {
            isLoadingMessages &&
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                minHeight: 150,
              }}
            >
              <CircularProgress size={24} />
            </Stack>
          }
          {
            !isLoadingMessages &&
            hasLoadedMessages &&
            messagesToDisplay.length === 0 &&
            <Typography color="text.secondary">
              {translate('caregiver.CaregiverDetail.ClientConnections.Chat.NoMessages')}
            </Typography>
          }
          {
            !isLoadingMessages &&
            hasLoadedMessages &&
            messagesToDisplay.length > 0 &&
            <Box
              ref={(element) => {
                messageContainerRefs.current[chat.conversationRn] = element;
              }}
              sx={{
                maxHeight: 420,
                overflowY: 'auto',
                pr: 1,
              }}
            >
              <Stack spacing={1}>
                {
                  messagesToDisplay.map((message) => {
                    const isOwnMessage = message.requesterRn === user?.userRn;
                    const requesterName = [
                      message.requester.firstName,
                      message.requester.lastName,
                    ].filter(Boolean).join(' ');

                    return (
                      <Stack
                        key={message.messageRn || `${message.requesterRn}-${message.createdAt}`}
                        ref={(element) => {
                          if (message.messageRn) {
                            messageRefs.current[message.messageRn] = element;
                          }
                        }}
                        sx={{
                          borderRadius: 2,
                          outline: message.messageRn === targetMessageRn && highlightTargetMessage ? '3px solid' : '0 solid',
                          outlineColor: 'warning.main',
                          transition: 'outline-width 300ms ease',
                        }}
                      >
                        <MessageBubble
                          isOwn={isOwnMessage}
                          fullName={requesterName || translate('caregiver.CaregiverDetail.ClientConnections.Chat.UnknownSender')}
                          timestampLabel={message.createdAt ? shortDateTime(message.createdAt) : ''}
                          messageText={message.messageText}
                          files={message.files}
                        />
                      </Stack>
                    );
                  })
                }
              </Stack>
            </Box>
          }
          {
            !isLoadingMessages &&
            expandedConversationRn === chat.conversationRn &&
            <MessageComposer
              conversationRn={chat.conversationRn}
              onSend={sendMessage}
              placeholder={translate('caregiver.CaregiverDetail.ClientConnections.Chat.Placeholder')}
              sendLabel={translate('caregiver.CaregiverDetail.ClientConnections.Chat.Send')}
              sendFailedLabel={translate('caregiver.CaregiverDetail.ClientConnections.Chat.SendFailed')}
            />
          }
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      {
        conversationsLoading &&
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            minHeight: 300,
          }}
        >
          <CircularProgress />
        </Stack>
      }
      {
        !conversationsLoading &&
        includeSupportChats &&
        supportChats.length === 0 &&
        <Stack sx={{gap: 0.75, alignItems: 'flex-start', px: 2, pt: 2}}>
          <Button
            disabled={isCreatingSupportChat}
            onClick={createSupportChat}
            variant='outlined'
          >
            {translate('caregiver.CaregiverDetail.ClientConnections.SupportChat.Start')}
          </Button>
          {
            createSupportChatFailed &&
            <Typography sx={{fontSize: '12px', fontWeight: 400, color: 'error.main'}}>
              <Translate expression='caregiver.CaregiverDetail.ClientConnections.SupportChat.StartFailed' />
            </Typography>
          }
        </Stack>
      }
      {
        !conversationsLoading &&
        visibleChatCount === 0 &&
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            minHeight: 300,
          }}
        >
          <Typography color="text.secondary">
            <Translate expression='caregiver.CaregiverDetail.ClientConnections.Chat.Empty' />
          </Typography>
        </Stack>
      }
      {
        !conversationsLoading &&
        visibleChatCount > 0 &&
        <Box sx={{p: 2}}>
          {
            supportChats.length > 0 &&
            <Paper
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                boxShadow: 'none',
                mb: 2,
              }}
            >
              {
                supportChats.map((chat) => {
                  return renderChatAccordion(chat);
                })
              }
            </Paper>
          }
          {
            clientSections.map((section) => {
              return (
                <Paper
                  key={section.clientRn}
                  sx={{
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    boxShadow: 'none',
                  }}
                >
                  <Box sx={{px: 2, py: 1.5}}>
                    <Typography sx={{fontWeight: 600}}>
                      {section.clientName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: 'text.secondary',
                      }}
                    >
                      {translate('caregiver.CaregiverDetail.ClientConnections.Chat.CareRecipient', {
                        name: section.careRecipientName,
                      })}
                    </Typography>
                  </Box>
                  <Divider />
                  {
                    section.chats.map((chat) => {
                      return renderChatAccordion(chat);
                    })
                  }
                </Paper>
              );
            })
          }
          {
            unassignedChats.length > 0 &&
            <Paper
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                boxShadow: 'none',
              }}
            >
              <Box sx={{px: 2, py: 1.5}}>
                <Typography sx={{fontWeight: 600}}>
                  {translate('caregiver.CaregiverDetail.ClientConnections.Chat.UnassignedChats')}
                </Typography>
              </Box>
              <Divider />
              {
                unassignedChats.map((chat) => {
                  return renderChatAccordion(chat);
                })
              }
            </Paper>
          }
        </Box>
      }
    </Box>
  );
};

ClientChats.propTypes = {
  caregiverRn: PropTypes.string.isRequired,
  caregiverData: PropTypes.object.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
  caregiverChatConversations: PropTypes.array.isRequired,
  caregiverChatConversationsLoading: PropTypes.bool.isRequired,
  caregiverChatMessages: PropTypes.object.isRequired,
  caregiverChatMessagesLoading: PropTypes.object.isRequired,
  filterClientRn: PropTypes.string,
  includeSupportChats: PropTypes.bool,
  user: PropTypes.object,
};

ClientChats.defaultProps = {
  includeSupportChats: true,
};

const mapStateToProps = (state) => ({user: state.user});

export default connect(mapStateToProps, dispatch)(React.memo(ClientChats));
